import {environment} from '$environments/environment'
import {
  CreatePostDTO,
  CreatePostDTOWithoutPhotos,
  CreatePostForm,
  CreatePostFormWithoutPhotos,
  HorseDTO,
  PostDisplay,
  PostDTO, PostFilters,
  PostStatus,
  UpdatePostForm
} from '$types'
import {HttpClient, HttpParams} from '@angular/common/http'
import {Injectable} from '@angular/core'
import {FileInput} from 'ngx-material-file-input'
import {Observable, Subject} from 'rxjs'
import {tap} from "rxjs/operators";

@Injectable({
  providedIn: 'root',
})
export class PostsService {
  private postsUrl = environment.apiUrl + '/rest/posts';
  private posts: PostDisplay[] = [];
  postChanged = new Subject<PostDisplay[]>();
  loadingChanged = new Subject<boolean>();
  postUpdated = new Subject<PostDisplay>();
  orderPost = new Subject<string>();
  page = 0;
  loading: boolean = true;

  constructor(
    private readonly http: HttpClient
  ) {
  }

  get(page: number = 0): Observable<PostDisplay[]> {
    const params = new HttpParams().set('page', page);
    return this.http.get<PostDisplay[]>(this.postsUrl, {params})
      .pipe(tap((posts) => {
        this.posts = posts.filter(
          (post) => post.status !== PostStatus.ARCHIVED
        );
        this.postChanged.next(this.posts);
      }));
  }

  public emitOrderPost(orderBy: string) {
    this.orderPost.next(orderBy);
  }

  getNextPostPage(page: number = 0): Observable<PostDisplay[]> {
    const params = new HttpParams().set('page', page);
    return this.http.get<PostDisplay[]>(this.postsUrl, {params}).pipe(
      tap((posts) => {
        this.posts = this.posts.concat(posts.filter((post) => post.status !== PostStatus.ARCHIVED));
      })
    );
  }

  getPostsWithFilters(filters: PostFilters) {
    this.changeLoadingState(true);
    this.page = -1;
    let params = new HttpParams();
    if (filters.horseName && filters.horseName.trim().length !== 0) {
      params = params.set("horse-name", filters.horseName);
    }

    if (filters.orderBy && filters.orderBy.trim().length !== 0) {
      params = params.set("order-by", filters.orderBy);
    }

    if (filters.status && filters.status.trim().length !== 0) {
      params = params.set("status", filters.status);
    }

    if (filters.category && filters.category.trim().length !== 0) {
      params = params.set("category", filters.category);
    }

    if (filters.orderBy && filters.orderBy.trim().length !== 0) {
      params = params.set("order-by", filters.orderBy);
    }

    if (filters.author && filters.author.trim().length !== 0) {
      params = params.set("author", filters.author);
    }

    return this.http.get<PostDisplay[]>(`${this.postsUrl}/filters`, {params}).pipe(
      tap((posts) => {
        this.posts.length = 0;
        this.postChanged.next(posts);
        this.changeLoadingState(false);
      })
    );
  }

  getPosts(): PostDisplay[] {
    return this.posts;
  }

  changeLoadingState(state: boolean) {
    this.loadingChanged.next(state);
    this.loading = state;
  }

  getAndOrderBy(sort: string): Observable<PostDisplay[]> {
    return this.http.get<PostDisplay[]>(this.postsUrl + "?order-by=" + sort).pipe(tap((posts) => {
      this.posts = posts.filter(
        (post) => post.status !== PostStatus.ARCHIVED
      );
      this.postChanged.next(this.posts);
    }));
  }

  findById(id: string): Observable<PostDisplay> {
    return this.http.get<PostDisplay>(this.postsUrl + '/' + id)
  }

  renouvele(id: string): Observable<PostDTO | null> {
    return this.http.put<PostDTO>(`${this.postsUrl}/${id}/renew`, null)
  }

  validate(id: string): Observable<PostDTO | null> {
    return this.http.put<PostDTO>(`${this.postsUrl}/${id}/validate`, null)
  }

  validateFree(id: string) {
    return this.http.put<PostDTO>(`${this.postsUrl}/${id}/validate`, null) //TODO: change this when payment fixed
  }

  invalidate(id: string): Observable<PostDTO | null> {
    return this.http.put<PostDTO>(`${this.postsUrl}/${id}/invalidate`, null)
  }

  delete(id: string) {
    const postUrl = `${this.postsUrl}/${id}`
    return this.http.delete(postUrl)
  }

  createAdmin(postToCreate: CreatePostForm, free: boolean): Observable<HorseDTO | null> {

    const formData = this.prepareCreateFormData(postToCreate)
//     formData.append('free', ''+free)
    return this.http.post<HorseDTO>(`${this.postsUrl}/admin`, formData)
  }

  createDraftAdmin(postToCreate: CreatePostFormWithoutPhotos, free: boolean): Observable<HorseDTO | null> {

    const formData = this.prepareCreateDraftFormData(postToCreate)
    formData.append('free', '' + free)
    return this.http.post<HorseDTO>(`${this.postsUrl}/admin`, formData)
  }

  create(postToCreate: CreatePostFormWithoutPhotos): Observable<HorseDTO | null> {

    const formData = this.prepareCreateFormData(postToCreate)

    return this.http.post<HorseDTO>(this.postsUrl, formData)
  }

  createDraft(postToCreate: CreatePostFormWithoutPhotos): Observable<HorseDTO | null> {

    const formData = this.prepareCreateDraftFormData(postToCreate)

    return this.http.post<HorseDTO>(this.postsUrl, formData)
  }

  addPhotoToPost(postId: string, file: File): Observable<PostDisplay> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.patch<PostDisplay>(`${this.postsUrl}/${postId}/addPhoto`, formData);
  }


  private prepareCreateDraftFormData(postToCreate: CreatePostFormWithoutPhotos) {
    postToCreate.status = PostStatus.DRAFT
    const newHorse: CreatePostDTOWithoutPhotos = {
      sire: postToCreate.horse.sire,
      id: '',
      idHorse: '',
      name: postToCreate.horse.name,
      birthYear: postToCreate.horse.birthYear,
      sex: postToCreate.horse.sex,
      raceRecord: postToCreate.horse.raceRecord,
      totalEarn: postToCreate.horse.totalEarn,
      mother: postToCreate.horse.mother,
      father: postToCreate.horse.father,
      location: postToCreate.horse.location,
      mailConfirmation: postToCreate.mailConfirmation,
      //photos: postToCreate.photos,
      horseRegistration: postToCreate.horseRegistration,
      certificateOfSale: postToCreate.certificateOfSale,
      extractKbis: postToCreate.extractKbis,
      certificationOfThePerson: postToCreate.certificationOfThePerson,
      category: postToCreate.category,
      commentsEn: postToCreate.commentsEn,
      commentsFr: postToCreate.commentsFr,
      idClient: postToCreate.idClient,
      sellPrice: postToCreate.sellPrice,
      status: postToCreate.status,
      taxableVat: postToCreate.taxableVat,
      toNegociate: postToCreate.toNegociate,
      email: postToCreate.contact.email,
      firstName: postToCreate.contact.firstName,
      isEnglish: postToCreate.contact.isEnglish,
      landlinePhoneNumber: postToCreate.contact.landlinePhoneNumber,
      lastName: postToCreate.contact.lastName,
      mobilePhoneNumber: postToCreate.contact.mobilePhoneNumber,
      role: postToCreate.contact.role
    }

    return this.postDTOToPostForm(newHorse)
  }


  private prepareCreateFormData(postToCreate: CreatePostFormWithoutPhotos) {
    postToCreate.status = PostStatus.WAITING_VALIDATION;
    const newHorse: CreatePostDTOWithoutPhotos = {
      sire: postToCreate.horse.sire,
      id: '',
      idHorse: '',
      name: postToCreate.horse.name,
      birthYear: postToCreate.horse.birthYear,
      sex: postToCreate.horse.sex,
      raceRecord: postToCreate.horse.raceRecord,
      totalEarn: postToCreate.horse.totalEarn,
      mother: postToCreate.horse.mother,
      father: postToCreate.horse.father,
      location: postToCreate.horse.location,
      mailConfirmation: postToCreate.mailConfirmation,
      //photos: postToCreate.photos,
      horseRegistration: postToCreate.horseRegistration,
      certificateOfSale: postToCreate.certificateOfSale,
      extractKbis: postToCreate.extractKbis,
      certificationOfThePerson: postToCreate.certificationOfThePerson,
      category: postToCreate.category,
      commentsEn: postToCreate.commentsEn,
      commentsFr: postToCreate.commentsFr,
      idClient: postToCreate.idClient,
      sellPrice: postToCreate.sellPrice,
      status: postToCreate.status,
      taxableVat: postToCreate.taxableVat,
      toNegociate: postToCreate.toNegociate,
      email: postToCreate.contact.email,
      firstName: postToCreate.contact.firstName,
      isEnglish: postToCreate.contact.isEnglish,
      landlinePhoneNumber: postToCreate.contact.landlinePhoneNumber,
      lastName: postToCreate.contact.lastName,
      mobilePhoneNumber: postToCreate.contact.mobilePhoneNumber,
      role: postToCreate.contact.role
    }

    return this.postDTOToPostForm(newHorse)
  }

  update(postToUpdate: UpdatePostForm, originalDoc: PostDisplay): Observable<HorseDTO | null> {

//     postToUpdate.status = PostStatus.DRAFT
    const horseToPatch: CreatePostDTO = {
      sire: postToUpdate.horse.sire,
      id: postToUpdate.id,
      idHorse: '',//<--- inutile
      name: postToUpdate.horse.name,
      birthYear: postToUpdate.horse.birthYear,
      sex: postToUpdate.horse.sex,
      raceRecord: postToUpdate.horse.raceRecord,
      totalEarn: postToUpdate.horse.totalEarn,
      mother: postToUpdate.horse.mother,
      father: postToUpdate.horse.father,
      location: postToUpdate.horse.location,
      mailConfirmation: postToUpdate.mailConfirmation,
      photos: postToUpdate.photos,
      horseRegistration: postToUpdate.horseRegistration,
      certificateOfSale: postToUpdate.certificateOfSale,
      extractKbis: postToUpdate.extractKbis,
      certificationOfThePerson: postToUpdate.certificationOfThePerson,
      category: postToUpdate.category,
      commentsEn: postToUpdate.commentsEn,
      commentsFr: postToUpdate.commentsFr,
      idClient: postToUpdate.idClient,
      sellPrice: postToUpdate.sellPrice,
      status: postToUpdate.status,
      taxableVat: postToUpdate.taxableVat,
      toNegociate: postToUpdate.toNegociate,
      email: postToUpdate.contact.email,
      firstName: postToUpdate.contact.firstName,
      isEnglish: postToUpdate.contact.isEnglish,
      landlinePhoneNumber: postToUpdate.contact.landlinePhoneNumber,
      lastName: postToUpdate.contact.lastName,
      mobilePhoneNumber: postToUpdate.contact.mobilePhoneNumber,
      role: postToUpdate.contact.role
    }
    delete horseToPatch.photos;
    const formData = this.postDTOToPostForm(horseToPatch, originalDoc)

    return this.http.put<HorseDTO>(`${this.postsUrl}/${horseToPatch.id}`, formData)
  }

  private postDTOToPostForm(dto: CreatePostDTOWithoutPhotos, originalDoc?: PostDisplay): FormData {
    const formData = new FormData()
    const formImageNames = [
      //'photos',
      'horseRegistration',
      'certificateOfSale',
      'extractKbis',
      'certificationOfThePerson',
    ]

    Object.entries(dto)
      .filter(e => !formImageNames.includes(e[0])) // on ne prend traite pas les images
      .forEach(e => {
        if (e[1] !== null) {
          formData.append(e[0], e[1] as string | Blob);
        }
      });

//    if (dto.photos) {
//      // si input vide, mais que photo déjà présente, on passe une variable pour dire qu'on garde celle en base.
//      if (!dto.photos.files && originalDoc?.photos) {
//        formData.append('photosKeep', 'true')
//      } else if (dto.photos.files) {
//        dto.photos.files.forEach(p => formData.append('photos[]', p))
//      }
//    }

    this.processImageToFormData(dto.horseRegistration,
      originalDoc?.horseRegistration,
      formData,
      'horseRegistration',
      'horseRegistrationKeep')
    this.processImageToFormData(dto.certificateOfSale,
      originalDoc?.certificateOfSale,
      formData,
      'certificateOfSale',
      'certificateOfSaleKeep')
    this.processImageToFormData(dto.extractKbis,
      originalDoc?.extractKbis,
      formData,
      'extractKbis',
      'extractKbisKeep')
    this.processImageToFormData(dto.certificationOfThePerson,
      originalDoc?.certificationOfThePerson,
      formData,
      'certificationOfThePerson',
      'certificationOfThePersonKeep')

    return formData
  }

  private processImageToFormData(imageInput: FileInput | undefined, originalImage: string | undefined, formData: FormData, imageName: string, imageKeep: string) {

    if (imageInput) {
      // si input vide, mais que photo déjà présente, on passe une variable pour dire qu'on garde celle en base.
      if (!imageInput.files && originalImage) {
        formData.append(imageKeep, 'true')
      } else if (imageInput.files) {
        formData.append(imageName, imageInput.files[0])
      }
    }
  }

  replacePost(updated: PostDisplay) {
    this.postUpdated.next(updated);
  }
}
